// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CaseDetail/intakeUtils.ts

import type {
  NavigatorCaseDetail,
  NavigatorCaseMatchResult,
  NavigatorCaseWorkflow,
  SelectOption,
} from "./types";

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export type CalendarDateParts = {
  year: number;
  month: number;
  day: number;
};

const ONTARIO_TIME_ZONE = "America/Toronto";

function isValidCalendarDate(year: number, month: number, day: number): boolean {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return false;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }

  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
}

/**
 * Extract a calendar DOB from YYYY-MM-DD or ISO-midnight strings.
 * Does not use local timezone getters on the parsed instant.
 */
export function parseDateOnlyParts(
  value: string | null | undefined
): CalendarDateParts | null {
  if (value === null || value === undefined) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!isValidCalendarDate(year, month, day)) return null;

  return { year, month, day };
}

export function getOntarioCalendarDateParts(
  now: Date = new Date()
): CalendarDateParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ONTARIO_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);

  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((entry) => entry.type === type);
    return Number(part?.value);
  };

  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
  };
}

export function calculateAgeFromDateOnly(
  dob: CalendarDateParts,
  today: CalendarDateParts
): number | null {
  let age = today.year - dob.year;
  const monthOffset = today.month - dob.month;

  if (monthOffset < 0 || (monthOffset === 0 && today.day < dob.day)) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

export function formatDateOnly(value: string | null | undefined): string {
  const parts = parseDateOnlyParts(value);
  if (!parts) return "—";

  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));

  return date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatStatus(status: string | null | undefined): string {
  switch (status) {
    case "New":
      return "New";
    case "Assigned":
      return "Assigned";
    case "InProgress":
      return "In progress";
    case "IntakeComplete":
      return "Intake complete";
    case "MatchReady":
      return "Match ready";
    case "ForwardedToProvider":
      return "Forwarded";
    case "Closed":
      return "Closed";
    default:
      return status || "—";
  }
}

export function formatSource(sourceType: string | null | undefined): string {
  switch (sourceType) {
    case "NavigatorPublicRequest":
      return "Public navigator request";
    case "NavigatorUpload":
      return "Uploaded list";
    case "OHT":
      return "OHT list";
    case "ManualNavigatorEntry":
      return "Manual entry";
    case "FutureHCC":
      return "Future HCC";
    default:
      return sourceType || "—";
  }
}

export function formatPatientName(caseDetail: NavigatorCaseDetail): string {
  return (
    caseDetail.patient.fullName ||
    [caseDetail.patient.firstName, caseDetail.patient.lastName]
      .filter(Boolean)
      .join(" ") ||
    "Unnamed patient"
  );
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return `${Number(value).toFixed(Number(value) % 1 === 0 ? 0 : 1)}%`;
}

export function formatJsonPreview(value: unknown): string {
  if (value === null || value === undefined) return "—";

  if (Array.isArray(value) && value.length === 0) return "—";

  if (typeof value === "string") return value || "—";

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "—";
  }
}

export function humanizeKey(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

export function isInternalNoEmailAddress(
  value: string | null | undefined
): boolean {
  return Boolean(value?.includes("@internal.acceptingmd.local"));
}

export function getPatientAgeYears(
  dateOfBirth: string | null | undefined,
  asOf: CalendarDateParts = getOntarioCalendarDateParts()
): number | null {
  const dob = parseDateOnlyParts(dateOfBirth);
  if (!dob) return null;

  return calculateAgeFromDateOnly(dob, asOf);
}

export function getPatientAge(
  dateOfBirth: string | null | undefined
): string {
  const age = getPatientAgeYears(dateOfBirth);
  return age === null ? "—" : `${age}`;
}

export function getDerivedAgeGroup(
  dateOfBirth: string | null | undefined,
  asOf: CalendarDateParts = getOntarioCalendarDateParts()
): string {
  const age = getPatientAgeYears(dateOfBirth, asOf);

  if (age === null) return "—";
  if (age < 1) return "Infant under 1";
  if (age < 13) return "Child";
  if (age < 18) return "Teen";
  if (age >= 75) return "Senior 75+";
  if (age >= 65) return "Senior 65+";
  return "Adult";
}

export function getPrimaryPhone(caseDetail: NavigatorCaseDetail): string {
  return (
    caseDetail.patient.navigatorContactPhone ||
    caseDetail.patient.phoneNumber ||
    caseDetail.patient.applicantPhone ||
    "—"
  );
}

export function getPrimaryEmail(caseDetail: NavigatorCaseDetail): string {
  if (
    caseDetail.patient.manualNoEmail ||
    isInternalNoEmailAddress(caseDetail.patient.email)
  ) {
    return "No patient email — navigator phone follow-up";
  }

  return caseDetail.patient.email || caseDetail.patient.applicantEmail || "—";
}

export function getContactStatus(caseDetail: NavigatorCaseDetail): string {
  if (caseDetail.patient.manualNoEmail) {
    return "Manual no-email workflow";
  }

  if (isInternalNoEmailAddress(caseDetail.patient.email)) {
    return "Internal placeholder email";
  }

  return "Email available";
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

export function readBoolean(
  record: Record<string, unknown>,
  key: string
): boolean {
  return record[key] === true;
}

export function readString(
  record: Record<string, unknown>,
  key: string
): string {
  const value = record[key];
  return typeof value === "string" ? value : "";
}

export function getNestedRecord(
  record: Record<string, unknown>,
  key: string
): Record<string, unknown> {
  const value = record[key];
  return isRecord(value) ? value : {};
}

export function getChoiceLabel(
  options: SelectOption[],
  value: string
): string {
  return options.find((option) => option.value === value)?.label || "—";
}

export function compactList(
  items: Array<string | null | undefined | false>
): string[] {
  return items.filter((item): item is string => Boolean(item));
}

export function toTextList(value: unknown): string[] {
  if (value === null || value === undefined) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;

        if (typeof item === "number" || typeof item === "boolean") {
          return String(item);
        }

        if (item && typeof item === "object") {
          const record = item as Record<string, unknown>;
          const possibleText =
            record.label ||
            record.name ||
            record.reason ||
            record.text ||
            record.value ||
            record.title;

          if (
            typeof possibleText === "string" ||
            typeof possibleText === "number" ||
            typeof possibleText === "boolean"
          ) {
            return String(possibleText);
          }
        }

        return formatJsonPreview(item);
      })
      .filter((item) => item && item !== "—");
  }

  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, itemValue]) => {
        if (itemValue === null || itemValue === undefined || itemValue === "") {
          return false;
        }

        if (Array.isArray(itemValue) && itemValue.length === 0) {
          return false;
        }

        return true;
      })
      .map(([key, itemValue]) => {
        if (
          typeof itemValue === "string" ||
          typeof itemValue === "number" ||
          typeof itemValue === "boolean"
        ) {
          return `${humanizeKey(key)}: ${String(itemValue)}`;
        }

        return `${humanizeKey(key)}: ${formatJsonPreview(itemValue)}`;
      });
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return [String(value)];
  }

  return [];
}

export function isExcludedMatch(result: NavigatorCaseMatchResult): boolean {
  const normalizedStatus = String(result.eligibilityStatus || "").toLowerCase();
  return normalizedStatus === "excluded" || normalizedStatus === "noteligible";
}

/** Usable algorithm results for primary recommendations (CN-R16). */
export function isUsableMatchResult(
  result: Pick<NavigatorCaseMatchResult, "eligibilityStatus"> | null | undefined
): boolean {
  const normalizedStatus = String(result?.eligibilityStatus || "")
    .trim()
    .toLowerCase();
  return normalizedStatus === "eligible" || normalizedStatus === "caution";
}

export function formatMatchScore(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  const n = Number(value);
  const formatted = n.toFixed(n % 1 === 0 ? 0 : 1);
  return `Match score ${formatted}/100`;
}

export type MatchResultsViewMode =
  | "neverMatched"
  | "matchingNeedsUpdate"
  | "noSuitableMatch"
  | "showRecommendations";

/**
 * Presentation mode for MatchResultsSection.
 * Canonical Next Action is authoritative (CN-R16).
 */
export function getMatchResultsViewMode(args: {
  nextActionCode?: string | null;
  hasLatestMatchRun: boolean;
  usablePrimaryCount: number;
}): MatchResultsViewMode {
  const code = String(args.nextActionCode || "").trim();

  if (code === "RunMatching") {
    return args.hasLatestMatchRun ? "matchingNeedsUpdate" : "neverMatched";
  }

  if (code === "NoSuitableMatch") {
    return "noSuitableMatch";
  }

  if (args.usablePrimaryCount > 0) {
    return "showRecommendations";
  }

  if (args.hasLatestMatchRun) {
    return "noSuitableMatch";
  }

  return "neverMatched";
}

export function getWorkspaceNotice(
  workflow: NavigatorCaseWorkflow | null | undefined
): string {
  if (!workflow) {
    return "Workspace permissions are loading from the server.";
  }

  if (workflow.isClosed) {
    return "This case is closed. Intake, matching, and forwarding are locked.";
  }

  if (workflow.isForwarded) {
    return "This case has already been forwarded to a provider.";
  }

  if (!workflow.isAssignedToMe) {
    return "This case is visible in your OHT scope, but actions are locked because it is not assigned to you.";
  }

  if (!workflow.hasLatestMatchRun) {
    return "Next step: complete or review intake, then run matching.";
  }

  if (!workflow.hasEligibleMatchResults) {
    return "A match run exists, but no eligible provider match is currently available.";
  }

  return "This case is ready for navigator action.";
}

export function getWorkflowSteps(caseDetail: NavigatorCaseDetail) {
  const currentStatus = caseDetail.status;

  return [
    {
      key: "Assigned",
      label: "Case assigned",
      isComplete: Boolean(caseDetail.assignedNavigatorUserId),
      isCurrent: currentStatus === "Assigned",
    },
    {
      key: "InProgress",
      label: "Intake in progress",
      isComplete: [
        "InProgress",
        "IntakeComplete",
        "MatchReady",
        "ForwardedToProvider",
        "Closed",
      ].includes(currentStatus),
      isCurrent: currentStatus === "InProgress",
    },
    {
      key: "IntakeComplete",
      label: "Intake complete",
      isComplete: [
        "IntakeComplete",
        "MatchReady",
        "ForwardedToProvider",
        "Closed",
      ].includes(currentStatus),
      isCurrent: currentStatus === "IntakeComplete",
    },
    {
      key: "MatchReady",
      label: "Match ready",
      isComplete: ["MatchReady", "ForwardedToProvider", "Closed"].includes(
        currentStatus
      ),
      isCurrent: currentStatus === "MatchReady",
    },
    {
      key: "ForwardedToProvider",
      label: "Forwarded to provider",
      isComplete: Boolean(caseDetail.submittedToDoctorAt),
      isCurrent: currentStatus === "ForwardedToProvider",
    },
  ];
}